/**
 * t471u: an Owner deletes a deactivated person permanently. The desktop Users page opens this as a dialog and the phone
 * person sheet as a bottom sheet; both use the same body, the same name check and the same wording as the iOS app
 * (packages/shared/src/member-deletion.ts). The API checks everything again.
 */
import { useId, useState } from 'react';
import { deleteNameMatches, MEMBER_DELETE_TEXT as T } from '@tradeoutlet/shared';
import { Button, Input, Modal } from '@/components/ui';
import { api, errorMessage } from '@/lib/api';
import { qk, queryClient } from '@/lib/queryClient';
import { useUi } from '@/lib/store';

/** The typed name, the busy state and the delete call, shared by the dialog and the sheet. */
export function useDeleteMember(ws: string | null | undefined, userId: string, name: string, onDeleted: () => void) {
  const toast = useUi(s => s.toast);
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const ok = deleteNameMatches(typed, name);
  const run = async () => {
    if (!ws || !ok || busy) return;
    setBusy(true); setError(null);
    try {
      await api.deleteMember(ws, userId, { confirm_name: typed });
      for (const key of [['members-all', ws], qk.members(ws), ['person', ws], ['wf', ws]]) void queryClient.invalidateQueries({ queryKey: key });
      toast(T.done, 'success');
      setTyped('');
      onDeleted();
    } catch (e) { setError(errorMessage(e) || T.failed); } finally { setBusy(false); }
  };
  const reset = () => { setTyped(''); setError(null); };
  return { typed, setTyped, busy, error, ok, run, reset };
}

type State = ReturnType<typeof useDeleteMember>;

/** What's deleted, what's kept, and the name box. */
export function DeleteMemberBody({ name, s }: { name: string; s: State }) {
  const id = useId();
  return (
    <div className="space-y-3 text-sm text-slate-600 dark:text-slate-300" data-testid="delete-member-body">
      <div>
        <p>{T.deletedLead}</p>
        <ul className="mt-1 list-disc space-y-0.5 pl-5">{T.deleted.map(l => <li key={l}>{l}</li>)}</ul>
      </div>
      <p>{T.kept}</p>
      <p>{T.elsewhere} {T.undo}</p>
      <div>
        <label htmlFor={id} className="mb-1 block font-medium text-slate-800 dark:text-slate-100">{T.label(name)}</label>
        <Input id={id} value={s.typed} onChange={e => s.setTyped(e.target.value)} autoComplete="off" autoCapitalize="off" spellCheck={false}
          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); void s.run(); } }} data-testid="delete-member-name" />
      </div>
      {s.error && <p role="alert" className="text-rose-700 dark:text-rose-300" data-testid="delete-member-error">{s.error}</p>}
    </div>
  );
}

export function DeleteMemberButtons({ s, onCancel, full }: { s: State; onCancel: () => void; full?: boolean }) {
  return (
    <div className={full ? 'grid grid-cols-2 gap-2' : 'flex justify-end gap-2'}>
      <Button variant="secondary" onClick={onCancel} disabled={s.busy} data-testid="delete-member-cancel">{T.cancel}</Button>
      <Button variant="danger" onClick={() => void s.run()} disabled={!s.ok} loading={s.busy} data-testid="delete-member-confirm">{T.confirm}</Button>
    </div>
  );
}

/** Desktop: the Users page's "Delete permanently" dialog. */
export function DeleteMemberDialog({ open, ws, userId, name, onClose, onDeleted }: {
  open: boolean; ws: string | null | undefined; userId: string; name: string; onClose: () => void; onDeleted: () => void;
}) {
  const s = useDeleteMember(ws, userId, name, onDeleted);
  const cancel = () => { if (s.busy) return; s.reset(); onClose(); };
  return (
    <Modal open={open} onClose={cancel} title={T.title(name)} footer={<DeleteMemberButtons s={s} onCancel={cancel} />}>
      <DeleteMemberBody name={name} s={s} />
    </Modal>
  );
}
